import React, { Fragment, FunctionComponent, useEffect, useRef, useState } from "react";

import { PAGE_TYPE } from "./page.types";
import { thunkUpdatePage } from "../redux/page.actions";
import getPageMeta from "../helpers/getPageMeta";
import getSameSiteChecker from "../helpers/getSameSiteChecker";
import UmbracoPageProps from "./UmbracoPageProps.interface";

import { CLOSE_ALL_OVERLAYS } from "layout/redux/overlay.actions";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import BlogPostPage from "pages/BlogPostPage/BlogPostPage";
import combineClasses from "helpers/combine-classes";
import ContentPage from "pages/ContentPage/ContentPage";
import ErrorPage from "pages/ErrorPage/ErrorPage";
import HomePage from "pages/HomePage/HomePage";
import isBrowserContext from "helpers/isBrowserContext";
import NotFoundPage from "pages/NotFoundPage/NotFoundPage";
import RootPage from "pages/RootPage/RootPage";
import useTagManager from "app/hooks/useTagManager";
import Transition from "pages/components/Transition/Transition";
import { useAppSelector } from "app/redux.store";
import useAnimation from "blocks/hooks/useAnimation";

const UmbracoPage: FunctionComponent<UmbracoPageProps> = (props) => {
    const match = useRouteMatch();
    const page = useSelector((state) => state.page.content);
    const history = useHistory();
    const settings = useSelector((state) => state.settings.content);

    const [referer, setReferer] = useState<PAGE_TYPE | undefined>();

    function unwrapProperty(key: string, obj: any) {
        let result = null;
        if (page && page[key]) {
            let value = page[key];
            result = value;
            if (typeof value === "string") {
                try {
                    let obValue = JSON.parse(value);
                    if (Array.isArray(obValue)) result = obValue[0];
                    else result = obValue;
                } catch (e) {}
            } else if (Array.isArray(value)) {
                result = value[0];
            }
        }
        return result;
    }

    let headerVisibility = unwrapProperty("headerVisibility", page);
    let footerVisibility = unwrapProperty("footerVisibility", page);

    const headerVisibilityClass = headerVisibility === "Overlapping" ? "overlapping-header" : headerVisibility === "Hidden" ? "hidden-header" : "relative-header";

    const footerVisibilityClass = footerVisibility === "Hidden" ? "hidden-footer" : "relative-footer";

    // const headerVisibilityClass = (typeof (page) !== "undefined" && (page as any).headerVisibility === "Overlapping") ? "overlapping-header" : (page && (page as any).headerVisibility === "Hidden") ? "hidden-header" : "relative-header";
    // const footerVisibilityClass = (typeof (page) !== "undefined" && (page as any).footerVisibility === "Hidden") ? "hidden-footer" : "relative-footer";

    const bodyLockedClass = typeof page !== "undefined" && page?.blocks?.find((x) => x?.content._contentType === "blockMundo") ? "is-locked" : undefined;

    // TODO: Declare type for UmbracoPage thunk actions
    const dispatch = useDispatch();
    const location = useLocation<any>();

    useTagManager(settings, history);
    const { shouldUseAnimations } = useAnimation(undefined, "animation1");

    // Page update - request
    useEffect(() => {
        // TODO: Dispatch UpdatePageRequest to show a loader while waiting (show if >250ms and show for minimum 250ms to avoid flashes.)
        // TODO: Use the UpdatePageRequest to check if history.back was used, and then fetch previous page synchronous from store (for scroll restoration)
        dispatch(thunkUpdatePage(match.url, history));
    }, [dispatch, match.url]);

    // Page update - callback
    useEffect(() => {
        if (isBrowserContext() && page && referer && getSameSiteChecker(page) !== getSameSiteChecker(referer)) {
            window.location.href = window.location.href;
        } else if (page?._url) {
            setReferer(page);
            // #SCROLL_RESTORATION: Resetting scrollbar on page changes to top page page
            if (isBrowserContext()) {
                window.scrollTo({
                    left: 0,
                    top: location.state?.scrollY ?? 0,
                    behavior: "instant",
                } as any); // "instant" behavior is working, just not supported as a type.
            }

            // #OVERLAY: Reset the redux drawer state to None when changing to a new page
            dispatch({
                type: CLOSE_ALL_OVERLAYS,
            });
        }
    }, [page]);

    const renderHreflang = (page: PAGE_TYPE) => {
        const fallback = page?._cultures ? (page._cultures["da"] ?? page._cultures["en"]) : undefined;

        return (
            <Fragment>
                {fallback ? (
                    <link
                        rel="alternate"
                        hrefLang="x-default"
                        href={fallback}
                    />
                ) : null}

                {page?._cultures &&
                    Object.keys(page._cultures).map((culture: any, index: number) => {
                        const hrefLang = culture;
                        const href = page._cultures ? page._cultures[culture] : undefined;

                        return (
                            <link
                                key={index}
                                rel="alternate"
                                hrefLang={hrefLang}
                                href={href}
                            />
                        );
                    })}
            </Fragment>
        );
    };

    const viewState = useAppSelector((state) => state.page.viewState);
    const transitionElementRef = useRef<any>(null);

    const renderHead = (page: PAGE_TYPE): JSX.Element => {
        // TODO: Open Graph
        // TODO: Canonical
        const { language, metaTitle, metaDescription, metaKeywords, metaCanonical, robots, socialTitle, socialDescription, socialImage, url, siteName } = getPageMeta(page, settings);

        return (
            <Helmet>
                <html lang={language} />
                <title>{metaTitle}</title>

                {/* Standard SEO meta tags */}
                <meta
                    name="title"
                    content={metaTitle}
                />
                <meta
                    name="description"
                    content={metaDescription}
                />
                <meta
                    name="keywords"
                    content={metaKeywords}
                />
                <meta
                    name="canonical"
                    content={metaCanonical}
                />
                <meta
                    name="robots"
                    content={robots}
                />

                {/* Canonical URL */}
                {metaCanonical && (
                    <link
                        rel="canonical"
                        href={metaCanonical}
                    />
                )}

                {renderHreflang(page)}

                {/* Open Graph */}
                <meta
                    property="og:title"
                    content={socialTitle || metaTitle}
                />
                <meta
                    property="og:description"
                    content={socialDescription || metaDescription}
                />
                <meta
                    property="og:image"
                    content={socialImage}
                />
                <meta
                    property="og:locale"
                    content={language}
                />
                <meta
                    property="og:site_name"
                    content={siteName}
                />
                <meta
                    property="og:type"
                    content="website"
                />
                <meta
                    property="og:url"
                    content={url}
                />
            </Helmet>
        );
    };

    const renderBody = (page: PAGE_TYPE): JSX.Element => {
        // TODO: Schema Breadcrumb

        switch (page?._contentType) {
            case "rootPage": {
                return <RootPage {...page} />;
            }
            case "homePage": {
                return <HomePage {...page} />;
            }
            case "contentPage": {
                return <ContentPage {...page} />;
            }
            case "blogPage": {
                return <BlogPostPage {...page} />;
            }
            case "notFoundPage": {
                return <NotFoundPage {...page} />;
            }
            case "errorPage": {
                return <ErrorPage />;
            }
            default: {
                return <Fragment></Fragment>;
            }
        }
    };

    return (
        <Fragment>
            {typeof page !== "undefined" ? (
                <Helmet
                    bodyAttributes={{
                        class: combineClasses(headerVisibilityClass, footerVisibilityClass, bodyLockedClass),
                    }}
                />
            ) : null}

            {/* Metadata */}
            {renderHead(page)}

            {/* Content */}
            {renderBody(page)}

            {/* Transition */}
            {isBrowserContext() && viewState === "TRANSITION" && shouldUseAnimations ? (
                <Transition
                    key={match.url}
                    ref={transitionElementRef}
                />
            ) : null}
        </Fragment>
    );
};

export default UmbracoPage;
